import { Activity, useEffect, useMemo, useRef, useState } from 'react'
import MapGL, { Layer, MapProvider, Source } from 'react-map-gl'
import MapboxCompare from 'mapbox-gl-compare'
import { BASE_INITIAL_VIEW, canviEscala, getInitialViewByWidth, getMinMaxForKey, MAPBOX_TOKEN, mapStyle } from './utils'
import BorderLayer from './components/BorderLayer'
import HeaderEmbed from '@common/HeaderEmbed'
import Llegenda from './components/Llegenda'
// import SelectorLayer from './components/SelectorLayer'
import Tooltip from './components/Tooltip'
// Textos
import textos from './assets/textos.json'
// Estils
import 'mapbox-gl/dist/mapbox-gl.css'
import 'mapbox-gl-compare/dist/mapbox-gl-compare.css'
import '@common/reset.css'
import './style.scss'

import dadesCsv from './assets/dades.csv'
import dadesCarlinsCsv from './assets/carlins.csv'

export const KEY_VOTS_ALIANCA = 'ac_share_vot'

const getMuniIdFromNatcode = (natcode) => {
	const value = String(natcode ?? '').trim()
	if (!value) return ''
	return value.slice(-5)
}

const getMuniIdCandidates = (natcode) => {
	const muniId = getMuniIdFromNatcode(natcode)
	if (!muniId) return []
	const numeric = Number.parseInt(muniId, 10)
	return Number.isFinite(numeric) ? [muniId, numeric] : [muniId]
}

const getCodIneCandidates = (...values) => {
	const candidates = new Set()

	for (const value of values) {
		const raw = String(value ?? '').trim()
		if (!raw) continue

		candidates.add(raw)

		if (raw.length > 5) {
			candidates.add(raw.slice(-5))
		}

		const numeric = Number.parseInt(raw, 10)
		if (Number.isFinite(numeric)) {
			candidates.add(String(numeric))
			candidates.add(String(numeric).padStart(5, '0'))
		}
	}

	return [...candidates]
}

const getFeatureStateIdCandidates = (id, isCarlins = false) => {
	if (id === null || id === undefined || id === '') return []

	const asString = String(id).trim()
	const asNumber = Number.parseInt(asString, 10)
	const candidates = [id, asString]

	if (Number.isFinite(asNumber)) {
		candidates.push(asNumber)
		if (isCarlins) {
			candidates.push(String(asNumber).padStart(5, '0'))
		}
	}

	return [...new Set(candidates.filter((value) => value !== ''))]
}

export default function App({ lang, preview, slug }) {
	let tipusMapa = slug
	let nomesAC = false
	if (slug === 'nomesac') {
		tipusMapa = null
		nomesAC = true
	}
	// Textos i dades
	const keysTipusDades = useMemo(() => (textos[lang]?.tipusDades ?? []).map((item) => `${item.key}`), [lang])
	// Refs
	const beforeMapRef = useRef(null)
	const afterMapRef = useRef(null)
	const containerRef = useRef(null)
	const compareRef = useRef(null)
	const hoveredIdsRef = useRef({
		before: null,
		after: null,
	})
	// Estats
	const [beforeMapLoaded, setBeforeMapLoaded] = useState(false)
	const [afterMapLoaded, setAfterMapLoaded] = useState(false)
	const [hoveredRow, setHoveredRow] = useState(null)
	const [selectedLayer, setSelectedLayer] = useState(tipusMapa ?? 'carlist_mean')
	const [valorsMinMax, setValorsMinMax] = useState({
		...Object.fromEntries(keysTipusDades.map((key) => [key, { min: null, max: null }])),
	})
	const [initialView, setInitialView] = useState(BASE_INITIAL_VIEW)
	const [isInitialViewReady, setIsInitialViewReady] = useState(false)
	const mapsLoaded = beforeMapLoaded && afterMapLoaded

	// Carregar dades CSV
	const [dades, setDades] = useState(dadesCsv.map((d) => ({ ...d, indep_collapse: d.indep_collapse * 100 })))
	const [dadesCarlins, setDadesCarlins] = useState(dadesCarlinsCsv)

	// Inicialitzar mapbox-gl-compare quan els dos mapes estiguin carregats
	useEffect(() => {
		if (nomesAC || !mapsLoaded || !beforeMapRef.current || !afterMapRef.current) return

		const beforeMap = beforeMapRef.current.getMap()
		const afterMap = afterMapRef.current.getMap()

		compareRef.current = new MapboxCompare(beforeMap, afterMap, containerRef.current, {
			orientation: 'vertical',
		})

		return () => {
			if (compareRef.current) {
				compareRef.current.remove?.()
				compareRef.current = null
			}
		}
	}, [mapsLoaded])

	// Dades mapa esquerra (percentatge vots Aliança) — expressió Mapbox
	useEffect(() => {
		if (!dades.length) return

		const minMax = getMinMaxForKey(dades, KEY_VOTS_ALIANCA)
		setValorsMinMax((prev) => ({
			...prev,
			[KEY_VOTS_ALIANCA]: minMax,
		}))
	}, [dades])

	// Actualitzar valorsMinMax per carlist_mean quan hi ha dadesCarlins
	useEffect(() => {
		if (!dadesCarlins.length) return

		const minMax = getMinMaxForKey(dadesCarlins, 'carlist_mean')
		setValorsMinMax((prev) => ({
			...prev,
			carlist_mean: minMax,
		}))
	}, [dadesCarlins])

	// Expressió de fill-opacity per al mapa dreta basada en selectedLayer
	const rightMapFillOpacity = useMemo(() => {
		if (selectedLayer === 'carlist_mean') return 0 // Aquesta capa usa la seva pròpia expressió

		const minMax = getMinMaxForKey(dades, selectedLayer)
		const entries = []

		for (const fila of dades) {
			const natcode = String(fila?.NATCODE ?? '').trim()
			const valor = Number.parseFloat(String(fila?.[selectedLayer] ?? '').replace(',', '.'))

			if (!natcode || !Number.isFinite(valor)) continue

			const opacity = canviEscala(valor, minMax?.max, minMax?.min)
			entries.push(natcode, opacity)
		}

		return ['match', ['get', 'NATCODE'], ...entries, 0]
	}, [dades, selectedLayer])

	// Dades mapa dreta (resta de dades) — només actualiza valorsMinMax
	useEffect(() => {
		if (!dades.length) return

		const minMaxPerKey = Object.fromEntries(keysTipusDades.map((key) => [key, getMinMaxForKey(dades, key)]))
		setValorsMinMax((prev) => ({
			...prev,
			...minMaxPerKey,
		}))
	}, [dades, keysTipusDades])
	// Calcular vista proporcional una sola vegada al carregar
	useEffect(() => {
		const ampleMapa = containerRef.current?.clientWidth ?? window.innerWidth
		const nextView = getInitialViewByWidth(ampleMapa)
		setInitialView(nextView)
		setIsInitialViewReady(true)
	}, [])

	// Configuració inicial del mapa i estils de les capes
	const divisio = {
		id: 'municipises',
		type: 'vector',
		url: 'mapbox://diariara.municipises',
		promoteId: 'NATCODE',
	}
	const divisio1922 = {
		id: 'municipis1922-23-06fijs',
		type: 'vector',
		url: 'mapbox://diariara.bsaj5qrd',
		promoteId: 'IdMuni',
	}
	const boundsLayer = {
		filter: ['==', '3409', ['slice', ['string', ['get', 'NATCODE']], 0, '3409'.length]],
	}

	const dadesIndex = useMemo(() => {
		const byNatcode = new Map()
		const byMuniId = new Map()
		for (const fila of dades) {
			const natcode = String(fila?.NATCODE ?? '').trim()
			const muniIds = getMuniIdCandidates(natcode)
			if (!natcode) continue
			byNatcode.set(natcode, fila)
			for (const muniId of muniIds) {
				byMuniId.set(muniId, fila)
			}
		}
		return { byNatcode, byMuniId }
	}, [dades])

	const dadesCarlinsIndex = useMemo(() => {
		const byCodIne = new Map()
		for (const fila of dadesCarlins) {
			const codIne = String(fila?.cod_ine ?? '').trim()
			if (!codIne) continue
			byCodIne.set(codIne, fila)
			const codIneNumeric = Number.parseInt(codIne, 10)
			if (Number.isFinite(codIneNumeric)) {
				byCodIne.set(String(codIneNumeric), fila)
			}
		}
		return { byCodIne }
	}, [dadesCarlins])

	const beforeMapFillOpacity = useMemo(() => {
		const minMax = getMinMaxForKey(dades, KEY_VOTS_ALIANCA)
		const entries = []

		for (const fila of dades) {
			const natcode = String(fila?.NATCODE ?? '').trim()
			const valor = Number.parseFloat(String(fila?.[KEY_VOTS_ALIANCA] ?? '').replace(',', '.'))

			if (!natcode || !Number.isFinite(valor)) continue

			const opacity = canviEscala(valor, minMax?.max, minMax?.min)
			entries.push(natcode, opacity)
		}

		return ['match', ['get', 'NATCODE'], ...entries, 0]
	}, [dades])

	const carlistFillOpacity = useMemo(() => {
		const minMax = getMinMaxForKey(dadesCarlins, 'carlist_mean')
		const entries = []
		for (const fila of dadesCarlins) {
			const codIne = String(fila?.cod_ine ?? '').trim()
			const valor = Number.parseFloat(String(fila?.carlist_mean ?? '').replace(',', '.'))
			if (!codIne || !Number.isFinite(valor)) continue
			const opacity = canviEscala(valor, minMax?.max, minMax?.min)
			const codIneNumeric = Number.parseInt(codIne, 10)
			entries.push(codIne, opacity)
			if (Number.isFinite(codIneNumeric) && String(codIneNumeric) !== codIne) {
				entries.push(String(codIneNumeric), opacity)
			}
		}

		if (!entries.length) return 0

		return ['match', ['to-string', ['coalesce', ['get', 'cod_ine'], ['get', 'IdMuni']]], ...entries, 0]
	}, [dadesCarlins])

	const updateHoverState = (feature) => {
		const isCarlins = selectedLayer === 'carlist_mean'
		const natcode = String(feature?.properties?.NATCODE ?? '').trim()
		const muniId = String(feature?.id ?? feature?.properties?.IdMuni ?? feature?.properties?.cod_ine ?? '').trim()

		let nextBeforeId = null
		let nextAfterId = null

		if (isCarlins) {
			const carlinsCandidates = getCodIneCandidates(muniId, natcode)
			nextAfterId = carlinsCandidates[0] || null
			nextBeforeId = natcode || null

			if (!nextBeforeId && nextAfterId) {
				const numericId = Number.parseInt(nextAfterId, 10)
				const filaMunicipi =
					dadesIndex.byMuniId.get(nextAfterId) ??
					(Number.isFinite(numericId) ? dadesIndex.byMuniId.get(numericId) : null)
				nextBeforeId = String(filaMunicipi?.NATCODE ?? '').trim() || null
			}
		} else {
			const sharedId = feature?.id ?? feature?.properties?.NATCODE ?? feature?.properties?.IdMuni ?? null
			nextBeforeId = sharedId
			nextAfterId = sharedId
		}

		const beforeMap = beforeMapRef.current?.getMap()
		const afterMap = afterMapRef.current?.getMap()
		const targets = [
			beforeMap
				? {
						map: beforeMap,
						source: 'municipises',
						sourceLayer: 'municipises',
						hoverKey: 'before',
						nextId: nextBeforeId,
						isCarlins: false,
					}
				: null,
			afterMap
				? {
						map: afterMap,
						source: isCarlins ? 'municipis1922-23-06fijs' : 'municipises',
						sourceLayer: isCarlins ? 'municipis1922-23-06fijs' : 'municipises',
						hoverKey: 'after',
						nextId: nextAfterId,
						isCarlins,
					}
				: null,
		].filter(Boolean)
		if (!targets.length) return

		for (const target of targets) {
			if (!target.map.getSource(target.source)) continue

			const prevId = hoveredIdsRef.current[target.hoverKey]
			const nextId = target.nextId
			const prevIds = getFeatureStateIdCandidates(prevId, target.isCarlins)
			const nextIds = getFeatureStateIdCandidates(nextId, target.isCarlins)

			if (prevId && prevId !== nextId) {
				for (const idCandidate of prevIds) {
					target.map.setFeatureState(
						{ source: target.source, sourceLayer: target.sourceLayer, id: idCandidate },
						{ hover: false },
					)
				}
			}

			if (nextId && prevId !== nextId) {
				for (const idCandidate of nextIds) {
					target.map.setFeatureState(
						{ source: target.source, sourceLayer: target.sourceLayer, id: idCandidate },
						{ hover: true },
					)
				}
			}

			hoveredIdsRef.current[target.hoverKey] = nextId || null
		}

		const nextId = isCarlins ? nextAfterId : nextBeforeId

		if (!nextId) {
			setHoveredRow(null)
			return
		}

		// Buscar dades segons selectedLayer
		let fila = null
		let dadaVots = null
		if (selectedLayer === 'carlist_mean') {
			const codIneCandidates = getCodIneCandidates(nextAfterId, nextBeforeId, nextId)
			for (const candidate of codIneCandidates) {
				fila = dadesCarlinsIndex.byCodIne.get(candidate)
				if (fila) break
			}

			if (nextBeforeId) {
				dadaVots = dadesIndex.byNatcode.get(nextBeforeId)
			}

			if (!dadaVots) {
				for (const candidate of codIneCandidates) {
					dadaVots = dadesIndex.byMuniId.get(candidate)
					if (dadaVots) break
				}
			}
		} else {
			const normalizedNextId = Number.parseInt(String(nextId), 10)
			fila =
				dadesIndex.byNatcode.get(nextId) ??
				dadesIndex.byMuniId.get(nextId) ??
				dadesIndex.byNatcode.get(String(nextId)) ??
				dadesIndex.byMuniId.get(String(nextId)) ??
				(Number.isFinite(normalizedNextId) ? dadesIndex.byMuniId.get(normalizedNextId) : null)
		}

		setHoveredRow(
			fila
				? {
						NATCODE: nextBeforeId || nextId,
						nom: String(fila?.nom ?? dadaVots?.nom ?? '').trim(),
						[KEY_VOTS_ALIANCA]:
							selectedLayer === 'carlist_mean'
								? (dadaVots?.[KEY_VOTS_ALIANCA] ?? null)
								: fila?.[KEY_VOTS_ALIANCA],
						carlist_mean: selectedLayer === 'carlist_mean' ? fila?.carlist_mean : null,
						...Object.fromEntries(keysTipusDades.map((key) => [key, fila?.[key]])),
					}
				: {
						NATCODE: nextId,
						nom: String(
							feature?.properties?.Muni ?? feature?.properties?.nom ?? feature?.properties?.NOM ?? '',
						).trim(),
						noCsv: true,
					},
		)
	}

	return (
		<>
			<Activity mode={preview ? 'visible' : 'hidden'}>
				<HeaderEmbed allowHomeEmbed={false} />
			</Activity>
			<MapProvider>
				{/* <SelectorLayer
					selected={selectedLayer}
					onChange={setSelectedLayer}
					lang={lang}
					valorsMinMax={valorsMinMax}
				/> */}
				<div ref={containerRef} className='contenidor-mapa'>
					{isInitialViewReady && (
						<>
							{/* Mapa esquerra (Before) */}
							<MapGL
								id='mapa_immigracio_before'
								ref={beforeMapRef}
								mapboxAccessToken={MAPBOX_TOKEN}
								projection='mercator'
								cursor='pointer'
								initialViewState={initialView}
								style={{
									position: 'absolute',
									width: '100%',
									height: '100%',
								}}
								mapStyle={mapStyle}
								attributionControl={false}
								cooperativeGestures={true}
								reuseMaps
								interactiveLayerIds={['municipises-fill']}
								locale={textos[lang]?.mapbox}
								onMouseMove={(event) => {
									const hoveredFeature = event.features?.[0]
									updateHoverState(hoveredFeature)
								}}
								onMouseLeave={() => {
									updateHoverState(null)
								}}
								onLoad={() => {
									setBeforeMapLoaded(true)
								}}
							>
								<Source {...divisio}>
									<Layer
										id='municipises-fill'
										source-layer='municipises'
										type='fill'
										beforeId='land-structure-line'
										paint={{
											'fill-color': '#005DAD',
											'fill-opacity': beforeMapFillOpacity,
										}}
										{...boundsLayer}
									/>
									<BorderLayer
										id='border-municipises'
										source='municipises'
										sourceLayer='municipises'
									/>
								</Source>
							</MapGL>

							{/* Mapa dreta (After) */}
							{!nomesAC && (
								<MapGL
									id='mapa_immigracio_after'
									ref={afterMapRef}
									mapboxAccessToken={MAPBOX_TOKEN}
									projection='mercator'
									cursor='pointer'
									initialViewState={initialView}
									style={{
										position: 'absolute',
										width: '100%',
										height: '100%',
									}}
									mapStyle={mapStyle}
									attributionControl={false}
									cooperativeGestures={true}
									reuseMaps
									interactiveLayerIds={
										selectedLayer === 'carlist_mean'
											? ['municipises-1922-fill-after']
											: ['municipises-fill-after']
									}
									locale={textos[lang]?.mapbox}
									onMouseMove={(event) => {
										const hoveredFeature = event.features?.[0]
										updateHoverState(hoveredFeature)
									}}
									onMouseLeave={() => {
										updateHoverState(null)
									}}
									onLoad={() => {
										setAfterMapLoaded(true)
									}}
								>
									{selectedLayer === 'carlist_mean' ? (
										<Source {...divisio1922}>
											<Layer
												id='municipises-1922-fill-after'
												source-layer='municipis1922-23-06fijs'
												type='fill'
												beforeId='land-structure-line'
												paint={{
													'fill-color': '#000',
													'fill-opacity': carlistFillOpacity,
												}}
											/>
											<BorderLayer
												id='border-municipises-1922-after'
												source='municipis1922-23-06fijs'
												sourceLayer='municipis1922-23-06fijs'
											/>
										</Source>
									) : (
										<Source {...divisio}>
											<Layer
												id='municipises-fill-after'
												source-layer='municipises'
												type='fill'
												beforeId='land-structure-line'
												paint={{
													'fill-color': '#000',
													'fill-opacity': rightMapFillOpacity,
												}}
												{...boundsLayer}
											/>
											<BorderLayer
												id='border-municipises-after'
												source='municipises'
												sourceLayer='municipises'
											/>
										</Source>
									)}
								</MapGL>
							)}
						</>
					)}
					{hoveredRow && (
						<Tooltip
							nom={hoveredRow.nom}
							vots={hoveredRow?.[KEY_VOTS_ALIANCA] ?? null}
							selectedLayer={nomesAC ? null : selectedLayer}
							valorLayer={hoveredRow?.[selectedLayer] ?? null}
							lang={lang}
							noCsv={hoveredRow.noCsv}
						/>
					)}
					<Llegenda selectedLayer={nomesAC ? null : selectedLayer} valorsMinMax={valorsMinMax} lang={lang} />
				</div>
			</MapProvider>
			{!nomesAC && (
				<p className='font'>
					{textos[lang].font} {textos[lang].tipusDades.find((dada) => dada.key === selectedLayer)?.font}
				</p>
			)}
		</>
	)
}
